import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search, Home, LogOut, RefreshCw } from "lucide-react";
import API_URL from '../config';
import LoadingScreen from '../components/LoadingScreen';
import './Home.css';
import './Explore.css';

const Explore = () => {
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [faculty, setFaculty] = useState([]);
    const [filteredFaculty, setFilteredFaculty] = useState([]);
    
    // Initialize states from sessionStorage if available
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem("explore_activeTab") || "students");
    const [dept, setDept] = useState(() => sessionStorage.getItem("explore_dept") || "");
    const [year, setYear] = useState(() => sessionStorage.getItem("explore_year") || "");
    const [section, setSection] = useState(() => sessionStorage.getItem("explore_section") || "");
    const [nameSearch, setNameSearch] = useState(() => sessionStorage.getItem("explore_nameSearch") || "");
    
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
    const isAdminOrCoAdmin = user.email === "24071a6691@vnrvjiet.in" || user.email === "24071a6679@vnrvjiet.in" || user.role === 'admin';

    const depts = ["CSE", "CSE-AIML&IOT", "AIDS", "CSBS", "IT", "ECE", "EEE", "EIE", "MECH", "CIVIL", "BIOTECH", "DS", "CYS", "R&AI"];
    const years = ["1st year", "2nd year", "3rd year", "4th year"];
    const yearLabels = { "1st year": "1st Year", "2nd year": "2nd Year", "3rd year": "3rd Year", "4th year": "4th Year" };
    const sections = ["A", "B", "C", "D"];

    const homeRoute = user.role === 'faculty' ? '/faculty/home' : '/home';

    const handleLogout = () => {
        localStorage.removeItem("vjconnect_user");
        navigate("/");
    };

    // Synchronize filter states to sessionStorage
    useEffect(() => {
        sessionStorage.setItem("explore_activeTab", activeTab);
        sessionStorage.setItem("explore_dept", dept);
        sessionStorage.setItem("explore_year", year);
        sessionStorage.setItem("explore_section", section);
        sessionStorage.setItem("explore_nameSearch", nameSearch);
    }, [activeTab, dept, year, section, nameSearch]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [studentRes, facultyRes] = await Promise.all([
                fetch(`${API_URL}/users`),
                fetch(`${API_URL}/faculty`)
            ]);
            if (studentRes.ok) {
                const data = await studentRes.json();
                const list = Array.isArray(data) ? data : (data.users || []);
                setStudents(list);
            }
            if (facultyRes.ok) {
                const data = await facultyRes.json();
                const list = Array.isArray(data) ? data : (data.faculty || []);
                setFaculty(list);
            }
        } catch (err) {
            console.error("Fetch error", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (activeTab === "students") {
            let filtered = students;
            if (dept) filtered = filtered.filter(s => s.department && s.department.toLowerCase() === dept.toLowerCase());
            if (year) filtered = filtered.filter(s => String(s.year).trim() === String(year).trim());
            if (section) filtered = filtered.filter(s => s.section && s.section.toUpperCase() === section.toUpperCase());
            if (nameSearch) {
                const lower = nameSearch.toLowerCase();
                filtered = filtered.filter(s =>
                    (s.name && s.name.toLowerCase().includes(lower)) ||
                    (s.rollNo && s.rollNo.toLowerCase().includes(lower))
                );
            }
            
            filtered.sort((a, b) => {
                // Prioritize search prefix if searching
                if (nameSearch) {
                    const lower = nameSearch.toLowerCase();
                    const aStarts = (a.name || "").toLowerCase().startsWith(lower) || (a.rollNo || "").toLowerCase().startsWith(lower);
                    const bStarts = (b.name || "").toLowerCase().startsWith(lower) || (b.rollNo || "").toLowerCase().startsWith(lower);
                    if (aStarts && !bStarts) return -1;
                    if (!aStarts && bStarts) return 1;
                }
                
                // Sort by Section first
                const secA = (a.section || "").toLowerCase();
                const secB = (b.section || "").toLowerCase();
                if (secA !== secB) return secA.localeCompare(secB);
                
                // Then sort by Roll Number (strictly alphanumeric so numbers precede letters)
                const rollA = (a.rollNo || "").toLowerCase();
                const rollB = (b.rollNo || "").toLowerCase();
                if (rollA < rollB) return -1;
                if (rollA > rollB) return 1;
                return 0;
            });
            setFilteredStudents(filtered);
        } else {
            let filtered = faculty;
            if (dept) filtered = filtered.filter(f => (f.work_dept || f.department || "").toLowerCase() === dept.toLowerCase());
            if (nameSearch) {
                const lower = nameSearch.toLowerCase();
                filtered = filtered.filter(f =>
                    (f.name && f.name.toLowerCase().includes(lower)) ||
                    (f.emp_id && String(f.emp_id).toLowerCase().includes(lower))
                );
                filtered.sort((a, b) => {
                    const aStarts = (a.name || "").toLowerCase().startsWith(lower);
                    const bStarts = (b.name || "").toLowerCase().startsWith(lower);
                    if (aStarts && !bStarts) return -1;
                    if (!aStarts && bStarts) return 1;
                    return 0;
                });
            }
            setFilteredFaculty(filtered);
        }
    }, [dept, year, section, nameSearch, students, faculty, activeTab]);

    const handleProfileOpen = (email) => {
        if (!email) return;
        if (activeTab === "faculty") {
            navigate(`/faculty/profile?email=${email}`);
        } else {
            const rollNo = email.split('@')[0];
            navigate(`/${rollNo}`);
        }
    };

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper">

                {/* ── Top bar: Back | Title+Count | Home+Logout ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {/* Back */}
                    <button
                        onClick={() => navigate(homeRoute)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                            padding: '0.5rem 1rem', borderRadius: '10px',
                            border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                            color: 'var(--text-main)', cursor: 'pointer', fontWeight: '600',
                            fontSize: '0.9rem', transition: 'all 0.2s', flexShrink: 0
                        }}
                    >
                        <ChevronLeft size={18} /> Back
                    </button>

                    {/* Title */}
                    <div style={{ flex: 1, textAlign: 'center', minWidth: '100px' }}>
                        <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Explore</h1>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.15rem' }}>
                            {activeTab === "students"
                                ? `${filteredStudents.length} ${filteredStudents.length === 1 ? 'student' : 'students'} found`
                                : `${filteredFaculty.length} ${filteredFaculty.length === 1 ? 'faculty' : 'faculty members'} found`}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button
                            onClick={() => navigate(homeRoute)}
                            title="Home"
                            style={{
                                width: 46, height: 46, borderRadius: '14px',
                                border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                                color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0
                            }}
                        >
                            <Home size={20} />
                        </button>
                        <button
                            onClick={handleLogout}
                            title="Logout"
                            style={{
                                width: 46, height: 46, borderRadius: '14px',
                                border: 'none', background: '#ef4444',
                                color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0
                            }}
                        >
                            <LogOut size={20} />
                        </button>
                    </div>
                </div>

                {/* ── Tabs ── */}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    {[
                        { id: 'students', label: 'Students' },
                        { id: 'faculty',  label: 'Faculty'  }
                    ].map(t => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                padding: '0.6rem 2rem', borderRadius: '20px',
                                border: '1px solid var(--glass-border)',
                                background: activeTab === t.id ? 'var(--accent-color)' : 'var(--glass-bg)',
                                color: activeTab === t.id ? 'white' : 'var(--text-main)',
                                cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s'
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ── Filters ── */}
                <div className="filters-container">
                    <div className="search-input-group" style={{ maxWidth: '300px' }}>
                        <Search className="search-icon" size={24} />
                        <input
                            type="text"
                            className="search-input-modern"
                            placeholder="Search by name..."
                            value={nameSearch}
                            onChange={e => setNameSearch(e.target.value)}
                            style={{ padding: '0.8rem 1rem 0.8rem 2.5rem' }}
                        />
                    </div>

                    <select value={dept} onChange={e => {
                        const val = e.target.value;
                        setDept(val);
                        if (val === "CSE-AIML&IOT" && section === "D") {
                            setSection("IOT");
                        }
                    }} className="modern-select">
                        <option value="">All Departments</option>
                        {depts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>

                    {activeTab === "students" && (
                        <select value={year} onChange={e => setYear(e.target.value)} className="modern-select">
                            <option value="">All Years</option>
                            {years.map(y => <option key={y} value={y}>{yearLabels[y]}</option>)}
                        </select>
                    )}

                    {activeTab === "students" && (
                        <select value={section} onChange={e => setSection(e.target.value)} className="modern-select">
                            <option value="">All Sections</option>
                            {(dept === "CSE-AIML&IOT" ? ["A", "B", "C", "IOT"] : sections).map(sec => <option key={sec} value={sec}>Section {sec}</option>)}
                        </select>
                    )}

                    <button 
                        onClick={fetchData} 
                        disabled={loading} 
                        className="modern-refresh-btn"
                        title="Refresh list from server"
                    >
                        <RefreshCw size={18} className={loading ? "spin-icon" : ""} />
                        <span>{loading ? "Refreshing..." : "Refresh"}</span>
                    </button>
                </div>

                {/* ── Grid ── */}
                {loading ? (
                    <LoadingScreen message="Explore VJ Link" />
                ) : (
                    <div className="explore-grid">
                        {activeTab === "students" ? (
                            filteredStudents.length === 0 ? (
                                <div className="no-results-text" style={{ gridColumn: '1/-1' }}>No student found matching your criteria.</div>
                            ) : (
                                filteredStudents.map(student => (
                                    <div key={student.email} className="student-card-explore" onClick={() => handleProfileOpen(student.email)}>
                                        <div className="student-avatar-xl">
                                            {student.profile_pic
                                                ? <img src={student.profile_pic} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                                : (student.name || '?').charAt(0).toUpperCase()
                                            }
                                        </div>
                                        <div className="student-info-explore">
                                            <h3>{student.name}</h3>
                                            <p>{student.rollNo}</p>
                                            <div className="dept-badge">{student.department} • {yearLabels[student.year] || student.year}</div>
                                        </div>
                                    </div>
                                ))
                            )
                        ) : (
                            filteredFaculty.length === 0 ? (
                                <div className="no-results-text" style={{ gridColumn: '1/-1' }}>No faculty found matching your criteria.</div>
                            ) : (
                                filteredFaculty.map(fac => (
                                    <div key={fac.email} className="student-card-explore" onClick={() => handleProfileOpen(fac.email)}>
                                        <div className="student-avatar-xl">
                                            {fac.profile_pic
                                                ? <img src={fac.profile_pic} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                                                : (fac.name || '?').charAt(0).toUpperCase()
                                            }
                                        </div>
                                        <div className="student-info-explore">
                                            <h3>{fac.name}</h3>
                                            <p>{fac.designation || 'Faculty'}</p>
                                            <div className="dept-badge">{fac.work_dept || fac.department || 'General'}</div>
                                        </div>
                                    </div>
                                ))
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Explore;
